"use client";

// Route-segment error boundary. Next renders this when any server or
// client component under app/ throws during render. We capture to
// PostHog with the BUILD_ID so the error is associated with this deploy.
//
// global-error.tsx handles the truly catastrophic case (failure during
// the root layout itself); this file covers everything else.

import { useEffect } from "react";
import posthog from "posthog-js";

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // posthog.captureException tags with the current distinctId + the
    // configured release id. Falls back to logging if posthog isn't
    // initialized (no NEXT_PUBLIC_POSTHOG_API_KEY in env).
    try {
      if (typeof posthog.captureException === "function") {
        // posthog-js v1.205+ takes (error, additionalProperties).
        // No distinctId arg — SDK uses the currently-identified person.
        posthog.captureException(error, {
          digest: error.digest,
          release: process.env.NEXT_PUBLIC_BUILD_ID,
          source: "app-error-boundary",
        });
      }
    } catch {
      console.error("[error-boundary]", error);
    }
  }, [error]);

  return (
    <div style={{
      maxWidth: 560, margin: "4rem auto", padding: "2rem 1.5rem",
      background: "var(--bg-elev)", border: "1px solid var(--border)",
      borderRadius: 8,
    }}>
      <h1 style={{ marginTop: 0 }}>Something went wrong.</h1>
      <p style={{ color: "var(--fg-muted)" }}>
        The error has been reported. You can try again, or head back to the
        dashboard.
      </p>
      {error.digest ? (
        <p style={{ color: "var(--fg-muted)", fontSize: "0.85rem" }}>
          Reference: <code>{error.digest}</code>
        </p>
      ) : null}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
        <button type="button" onClick={reset}>Try again</button>
        <a href="/">
          <button type="button">Back to dashboard</button>
        </a>
      </div>
    </div>
  );
}
